import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type CostCenterSchema, costCenterSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { CostCenterForm } from "@/forms/cost-center-form";
import { client } from "@/hono";

/** Props Interface */
interface EditCostCenterModalProps extends ModalProps {
	costCenterId: string;
}

export function EditCostCenterModal({ isOpen, onClose, costCenterId }: EditCostCenterModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.cost_centers[":costCenterId"].$get({
			param: { costCenterId },
		});
		const data = await responseHandler(response);
		return costCenterSchema.parse(data.costCenter);
	});
	const methods = useForm<CostCenterSchema>({
		defaultValues,
		resolver: resolver(costCenterSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: CostCenterSchema) {
		const response = await client.cost_centers[":costCenterId"].$patch({
			param: { costCenterId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["cost-centers", "incoming-invoices"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("cost_centers.edit_cost_center")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<CostCenterForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
